import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { baseUrlAdmin } from '../../store/apiConstants';
import Service from '../../services';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import BranchToSectionDD from '../BaseDropdownComponents/BranchToSectionDD';
import './FeeLedger.scss';

class FeeLedger extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.state = {
            loading: false,
            overviewData: [], // List of all students
            dropdownsData: {},
            globalFilter: null,
            // Pagination state
            totalRecords: 0,
            lazyParams: {
                first: 0,
                rows: 10,
                page: 0
            },
            // Detail View State
            showDetailDialog: false,
            selectedStudentId: null,
            detailLedgerData: null,
            detailStudentInfo: null,
            expandedRows: null // For payment transaction expansion
        };
        this.toast = React.createRef();
        this.dt = React.createRef();
    }

    toggleRow = (rowData) => {
        let expandedRows = this.state.expandedRows ? [...this.state.expandedRows] : [];
        const isExpanded = expandedRows.some(r => r.installmentId === rowData.installmentId);

        if (isExpanded) {
            expandedRows = expandedRows.filter(r => r.installmentId !== rowData.installmentId);
        } else {
            expandedRows.push(rowData);
        }
        this.setState({ expandedRows });
    }

    expanderTemplate = (rowData) => {
        if (rowData.paymentTransactions && rowData.paymentTransactions.length > 0) {
            const isExpanded = this.state.expandedRows && this.state.expandedRows.some(r => r.installmentId === rowData.installmentId);
            const iconClassName = isExpanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right';
            
            return (
                <button type="button" className="p-row-toggler p-link" onClick={() => this.toggleRow(rowData)}>
                    <span className={iconClassName}></span>
                </button>
            );
        }
        return null; 
    }

    rowExpansionTemplate = (data) => {
        return (
            <div className="p-3">
                <h5 className="mb-3">Payment Transaction History</h5>
                <DataTable value={data.paymentTransactions} className="p-datatable-sm" emptyMessage="No payment transactions found">
                    <Column field="receiptNo" header="Receipt No" style={{ minWidth: '120px' }} />
                    <Column field="transactionDate" header="Payment Date" body={(r) => r.transactionDate ? new Date(r.transactionDate).toLocaleDateString() : '-'} style={{ minWidth: '110px' }} />
                    <Column field="amountPaid" header="Amount Paid" body={(r) => this.currencyBody(r.amountPaid)} className="text-right" headerClassName="text-right" style={{ minWidth: '100px' }} />
                    <Column field="paymentMode" header="Mode" style={{ minWidth: '80px' }} />
                    <Column field="transactionId" header="Transaction ID" style={{ minWidth: '150px' }} body={(r) => r.transactionId || '-'} />
                    <Column field="chequeNo" header="Cheque/DD No" body={(r) => r.chequeNo || r.ddNo || '-'} style={{ minWidth: '120px' }} />
                    <Column field="bankName" header="Bank" body={(r) => r.bankName || '-'} style={{ minWidth: '120px' }} />
                    <Column field="remarks" header="Remarks" body={(r) => r.remarks || '-'} style={{ minWidth: '150px' }} />
                </DataTable>
            </div>
        );
    }

    componentDidMount() {
        // Check if studentId is passed in URL query params (from Critical Alerts)
        const urlParams = new URLSearchParams(window.location.search);
        const studentIdFromUrl = urlParams.get('studentId');
        
        if (studentIdFromUrl) {
            // Directly fetch and open this student's ledger detail
            this.fetchStudentLedgerFromAlert(studentIdFromUrl);
        } else {
            // Normal flow: fetch overview
            this.fetchLedgerOverview();
        }
    }

    setDropdownValues = (dropdownsData) => {
        this.setState(prevState => ({ 
            dropdownsData,
            lazyParams: { ...prevState.lazyParams, first: 0, page: 0 }
        }), () => {
             // Fetch whenever filter changes (even if cleared)
             this.fetchLedgerOverview();
        });
    }

    onPage = (event) => {
        this.setState({ lazyParams: event }, this.fetchLedgerOverview);
    }

    onSort = (event) => {
        this.setState(prevState => ({
            lazyParams: { ...prevState.lazyParams, ...event }
        }), this.fetchLedgerOverview);
    }

    fetchLedgerOverview = async () => {
        const { dropdownsData, lazyParams } = this.state;
        const academicYear = localStorage.getItem('userAcademicYear');
        let orgId = this.props.userInfo?.orgId;
        
        if (!orgId) {
             try {
                const storedUserInfo = JSON.parse(localStorage.getItem('userInfo'));
                if (storedUserInfo?.orgId) orgId = storedUserInfo.orgId;
            } catch (e) {}
        }

        if (!academicYear) return;

        this.setState({ loading: true });
        
        // Add pagination & sorting params
        let url = `${baseUrlAdmin}/fee-decision/ledger-overview?academicYear=${academicYear}&page=${lazyParams.page + 1}&limit=${lazyParams.rows}`;
        
        if (lazyParams.sortField) {
            url += `&sortField=${lazyParams.sortField}&sortOrder=${lazyParams.sortOrder}`;
        }

        if (orgId) url += `&orgId=${orgId}`;
        if (dropdownsData.classId) url += `&classId=${dropdownsData.classId}`;
        if (dropdownsData.sectionId) url += `&sectionId=${dropdownsData.sectionId}`;

        try {
            const response = await this.service.get(url, true);
            if (response?.status && response?.res?.success) {
                this.setState({
                    overviewData: response.res.data,
                    totalRecords: response.res.pagination ? response.res.pagination.total : 0,
                    loading: false
                });
            } else {
                this.showToast('error', 'Error', response?.res?.message || 'Failed to fetch ledger overview');
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error(error);
            this.setState({ loading: false });
            this.showToast('error', 'Error', 'Failed to fetch ledger data');
        }
    }

    fetchStudentLedger = async (studentId) => {
        const academicYear = localStorage.getItem('userAcademicYear');

        this.setState({ loading: true });
        const url = `${baseUrlAdmin}/fee-decision/ledger/${studentId}?academicYear=${academicYear}`;

        try {
            const response = await this.service.get(url, true);
            if (response?.status && response?.res?.success) {
                const data = response.res.data;
                this.setState(prevState => ({
                    detailLedgerData: data.installments,
                    detailStudentInfo: { ...prevState.detailStudentInfo, ...(data.summary || {}), ...(data.studentInfo || {}) },
                    loading: false,
                    showDetailDialog: true,
                    selectedStudentId: studentId
                }));
            } else {
                this.showToast('error', 'Error', 'Failed to fetch student detail');
                this.setState({ loading: false });
            }
        } catch (error) {
            this.setState({ loading: false });
        }
    }

    fetchStudentLedgerFromAlert = async (studentId) => {
        const academicYear = localStorage.getItem('userAcademicYear');

        this.setState({ loading: true });
        const url = `${baseUrlAdmin}/fee-decision/ledger/${studentId}?academicYear=${academicYear}`;

        try {
            const response = await this.service.get(url, true);
            if (response?.status && response?.res?.success) {
                const data = response.res.data;
                
                // Set student info and open detail dialog immediately
                this.setState({
                    detailLedgerData: data.installments,
                    detailStudentInfo: {
                        ...(data.summary || {}),
                        ...(data.studentInfo || {}),
                        studentId: studentId
                    },
                    selectedStudentId: studentId,
                    showDetailDialog: true,
                    loading: false,
                    overviewData: [] // Keep overview empty since we're focusing on one student
                });
                
                // Clean URL after opening detail (remove query param)
                window.history.replaceState({}, '', window.location.pathname);
            } else {
                this.showToast('error', 'Error', 'Failed to fetch student detail');
                this.setState({ loading: false });
                // Fallback to overview
                this.fetchLedgerOverview();
            }
        } catch (error) {
            console.error('Error fetching student ledger from alert:', error);
            this.showToast('error', 'Error', 'Failed to fetch student detail');
            this.setState({ loading: false });
            // Fallback to overview
            this.fetchLedgerOverview();
        }
    }

    viewDetails = (rowData) => {
        this.setState({ detailStudentInfo: rowData });
        this.fetchStudentLedger(rowData.studentId);
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    /** Templates for Overview Table **/
    currencyBody = (amount) => {
        return <span className="font-medium">₹{(amount || 0).toLocaleString()}</span>;
    }
    
    statusBody = (rowData) => {
         if (rowData.balance <= 0) return <span className="status-badge status-paid">PAID</span>;
         if (rowData.overdueDays > 0) return <span className="status-badge status-overdue">{rowData.overdueDays} DAYS OVERDUE</span>;
         return <span className="status-badge status-due">DUE</span>;
    }

    actionBody = (rowData) => {
        return (
            <Authorizer permId={PERMISSIONS.FDE_FEE_LEDGER}>
                <Button 
                    icon="pi pi-eye" 
                    className="p-button-text p-button-sm" 
                    onClick={() => this.viewDetails(rowData)} 
                    tooltip="View Ledger" 
                    tooltipOptions={{ position: 'left' }} 
                />
            </Authorizer>
        );
    }

    exportLedger = () => {
        const { detailLedgerData, detailStudentInfo } = this.state;
        if (!detailLedgerData || detailLedgerData.length === 0) return;

        const data = detailLedgerData.map(d => ({
            'INSTALLMENT': d.installmentName,
            'FEE TYPE': d.feeType || '-',
            'DUE DATE': d.dueDate ? new Date(d.dueDate).toLocaleDateString('en-GB') : '-',
            'AMT DUE': d.termAmount,
            'CONCESSION': d.concession,
            'NET AMOUNT': d.amount,
            'PAID': d.paid,
            // 'PAYMENT DATE': d.date ? new Date(d.date).toLocaleDateString('en-GB') : '-',
            'BALANCE': d.balance,
            'STATUS': d.status,
            'OVERDUE DAYS': d.overdueDays
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Ledger");
        
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'});
        
        saveAs(dataBlob, `Fee_Ledger_${detailStudentInfo?.name || 'Student'}.xlsx`);
    }

    exportLedgerPDF = () => {
        const { detailLedgerData, detailStudentInfo } = this.state;
        if (!detailLedgerData || detailLedgerData.length === 0) return;

        const doc = new jsPDF();
        
        // Header
        doc.setFontSize(18);
        doc.text('Fee Ledger Statement', 14, 20);
        
        doc.setFontSize(11);
        doc.text(`Student: ${detailStudentInfo?.name || '-'}`, 14, 30);
        doc.text(`Adm No: ${detailStudentInfo?.admissionNo || '-'}`, 14, 36);
        doc.text(`Class: ${detailStudentInfo?.className || '-'}`, 14, 42);
        
        const date = new Date().toLocaleDateString('en-GB');
        doc.text(`Date: ${date}`, 160, 30);

        // Simple table using text
        let y = 55;
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        
        // Columns setup
        const x = { inst: 14, type: 65, due: 95, amt: 120, paid: 140, bal: 160, status: 180 };

        doc.text('Installment', x.inst, y);
        doc.text('Fee Type', x.type, y);
        doc.text('Due Date', x.due, y);
        doc.text('Due', x.amt, y);
        doc.text('Paid', x.paid, y);
        doc.text('Bal', x.bal, y);
        doc.text('Status', x.status, y);
        
        doc.line(14, y + 2, 196, y + 2);
        y += 8;
        doc.setFont(undefined, 'normal');

        detailLedgerData.forEach(item => {
            if (y > 270) {
                doc.addPage();
                y = 20;
            }
            
            const dueDate = item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '-';
            
            // Truncate long names for this simple view
            const instName = item.installmentName.length > 25 ? item.installmentName.substring(0, 25) + '...' : item.installmentName;
            const typeName = (item.feeType || '-').substring(0, 15);

            doc.text(instName, x.inst, y);
            doc.text(typeName, x.type, y);
            doc.text(dueDate, x.due, y);
            doc.text(item.termAmount.toString(), x.amt, y);
            doc.text(item.paid.toString(), x.paid, y);
            doc.text(item.balance.toString(), x.bal, y);
            doc.text(item.status, x.status, y);

            y += 7;
        });

        // Summary
        y += 5;
        doc.line(14, y, 196, y);
        y += 10;
        doc.setFont(undefined, 'bold');
        doc.text(`Total Due: ${detailStudentInfo.totalDue}`, x.amt - 30, y);
        doc.text(`Paid: ${detailStudentInfo.totalPaid}`, x.paid - 5, y);
        doc.text(`Bal: ${detailStudentInfo.balance}`, x.bal - 5, y);

        doc.save(`Fee_Ledger_${detailStudentInfo?.name || 'Student'}.pdf`);
    }

    refreshDetail = () => {
        if (this.state.selectedStudentId) {
            this.fetchStudentLedger(this.state.selectedStudentId);
            this.showToast('info', 'Refreshed', 'Ledger data updated');
        }
    }

    viewProfile = () => {
        const { detailStudentInfo } = this.state;
        if (detailStudentInfo && detailStudentInfo.studentId) {
            if (this.props.navigate) {
                this.props.navigate(`/studentProfile?studentId=${detailStudentInfo.studentId}`);
            } else {
                 window.location.href = `/studentProfile?studentId=${detailStudentInfo.studentId}`;
            }
        }
    }

    /** Templates for Detail Table (Existing logic preserved) **/
    dateBodyTemplate = (rowData) => {
        const d = rowData.date || rowData.dueDate;
        return d ? new Date(d).toLocaleDateString('en-GB') : '-';
    }

    statusBodyTemplate = (rowData) => {
        // If Nothing Due and Nothing Paid (e.g. exempted or empty fee type), show '-'
        if (rowData.termAmount === 0 && rowData.paid === 0) {
            return <span>-</span>;
        }
        
        // Otherwise use the backend status with styling
        const status = rowData.status;
        if (status === 'PAID') return <span className="status-badge status-paid">PAID</span>;
        if (status === 'OVERDUE') return <span className="status-badge status-overdue">OVERDUE</span>;
        if (status === 'DUE') return <span className="status-badge status-due">DUE</span>;
        
        return <span>{status}</span>;
    }

    render() {
        const { loading, overviewData, showDetailDialog, detailLedgerData, globalFilter, detailStudentInfo } = this.state;

        const header = (
            <div className="flex justify-content-between align-items-center">
                <h3 className="m-0">Student Fee Ledgers</h3>
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        type="search" 
                        value={globalFilter || ''} 
                        onInput={(e) => this.setState({ globalFilter: e.target.value })} 
                        placeholder="Search by Name, Adm No..." 
                    />
                </span>
            </div>
        );
        
        return (
            <div className="fee-ledger-container">
                <Toast ref={this.toast} position="bottom-right" />
                {loading && <LoadingComponent />}
                
                <div className="card mb-3 p-3 surface-50">
                    <div className="grid align-items-end">
                        <div className="col-12 md:col-8">
                            <label className="block mb-2 font-bold text-gray-700">Filter by Class/Section</label>
                            <BranchToSectionDD setValues={this.setDropdownValues} />
                        </div>
                        <div className="col-12 md:col-4 text-right">
                            {/* Additional controls if needed */}
                        </div>
                    </div>
                </div>

                <div className="card p-0">
                    <DataTable 
                        value={overviewData} 
                        // globalFilter={globalFilter} // Server-side filtering not implemented yet
                        // globalFilterFields={['name', 'admissionNo', 'className', 'sectionName']}
                        header={header}
                        lazy
                        paginator 
                        first={this.state.lazyParams.first}
                        rows={this.state.lazyParams.rows}
                        totalRecords={this.state.totalRecords}
                        onPage={this.onPage}
                        onSort={this.onSort}
                        sortField={this.state.lazyParams.sortField}
                        sortOrder={this.state.lazyParams.sortOrder}
                        rowsPerPageOptions={[10, 25, 50]}
                        emptyMessage="Select a class to view student ledgers."
                        className="p-datatable-sm"
                        rowHover
                    >
                        <Column field="admissionNo" header="Adm No" sortable />
                        <Column field="studentName" header="Student Name" sortable />
                        <Column field="className" header="Class" sortable />
                        <Column field="sectionName" header="Section" sortable />
                        <Column field="totalDue" header="Total Fee" body={(r) => this.currencyBody(r.totalDue)} sortable  headerClassName="text-right" />
                        <Column field="totalPaid" header="Paid" body={(r) => this.currencyBody(r.totalPaid)} sortable  headerClassName="text-right" />
                        <Column field="balance" header="Balance" body={(r) => this.currencyBody(r.balance)} sortable  headerClassName="text-right" />
                        <Column field="paymentRatio" header="Payment Ratio (%)" body={(r) => <span className={r.paymentRatio < 50 ? 'text-red-500 font-bold' : 'text-green-500'}>{r.paymentRatio}%</span>} sortable  headerClassName="text-right" />
                        <Column header="Status" body={this.statusBody} sortable sortField="overdueDays" />
                        <Column body={this.actionBody} className="text-center" style={{ width: '5rem' }} />
                    </DataTable>
                </div>

                <Dialog 
                    visible={showDetailDialog} 
                    draggable={false}
                    style={{ width: '90vw' }} 
                    header={
                        <div className="flex justify-content-between align-items-center w-full pr-4">
                            <span>Detailed Fee Ledger</span>
                        </div>
                    }
                    onHide={() => this.setState({ showDetailDialog: false })}
                    maximizable
                >
                    {detailLedgerData && (
                        <div className="p-2">
                             {/* Aggregate Info Cards */}
                             {detailStudentInfo && (
                                <div className="grid mb-3">
                                    <div className="col-12 md:col-2">
                                        <div className="surface-100 p-2 border-round text-center">
                                            <div className="text-sm text-gray-600">Total Fee Due</div>
                                            <div className="font-bold text-blue-600">{this.currencyBody(detailStudentInfo.totalDue)}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-2">
                                        <div className="surface-100 p-2 border-round text-center">
                                            <div className="text-sm text-gray-600">Total Paid</div>
                                            <div className="font-bold text-green-600">{this.currencyBody(detailStudentInfo.totalPaid)}</div>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-2">
                                        <div className="surface-100 p-2 border-round text-center">
                                            <div className="text-sm text-gray-600">Balance</div>
                                            <div className="font-bold text-red-600">{this.currencyBody(detailStudentInfo.balance || (detailStudentInfo.totalDue - detailStudentInfo.totalPaid))}</div>
                                        </div>
                                    </div>
                                     <div className="col-12 md:col-2">
                                        <div className="surface-100 p-2 border-round text-center">
                                            <div className="text-sm text-gray-600">Payment Ratio</div>
                                            <div className={`font-bold ${detailStudentInfo.paymentRatio >= 75 ? 'text-green-600' : detailStudentInfo.paymentRatio >= 50 ? 'text-orange-600' : 'text-red-600'}`}>{detailStudentInfo.paymentRatio || 0}%</div>
                                        </div>
                                    </div>
                                     <div className="col-12 md:col-2">
                                        <div className="surface-100 p-2 border-round text-center">
                                            <div className="text-sm text-gray-600">Last Payment</div>
                                            <div className="font-bold text-teal-600">{detailStudentInfo.lastPaymentDate ? new Date(detailStudentInfo.lastPaymentDate).toLocaleDateString() : 'N/A'}</div>
                                        </div>
                                    </div>
                                     <div className="col-12 md:col-2 text-right white-space-nowrap">
                                        <Authorizer permId={PERMISSIONS.FDE_FEE_LEDGER}>
                                            <Button 
                                                icon="pi pi-refresh" 
                                                className="p-button-rounded p-button-text p-button-plain mr-1" 
                                                onClick={this.refreshDetail} 
                                                tooltip="Refresh Ledger" 
                                            />
                                        </Authorizer>
                                        <Authorizer permId={PERMISSIONS.FDE_EXPORT_FEE_LEDGER}>
                                            <Button 
                                                icon="pi pi-file-excel" 
                                                className="p-button-rounded p-button-text p-button-success mr-1" 
                                                tooltip="Export Excel" 
                                                onClick={this.exportLedger} 
                                            />
                                        </Authorizer>
                                        <Authorizer permId={PERMISSIONS.FDE_EXPORT_FEE_LEDGER}>
                                            <Button 
                                                icon="pi pi-file-pdf" 
                                                className="p-button-rounded p-button-text p-button-danger mr-1" 
                                                tooltip="Export PDF" 
                                                onClick={this.exportLedgerPDF} 
                                            />
                                        </Authorizer>
                                        <Button 
                                            icon="pi pi-id-card" 
                                            className="p-button-rounded p-button-text p-button-primary" 
                                            tooltip="View Profile" 
                                            onClick={this.viewProfile} 
                                        />
                                    </div>
                                </div>
                             )}

                             <DataTable 
                                value={detailLedgerData} 
                                responsiveLayout="scroll"
                                className="p-datatable-sm"
                                rowHover
                                showGridlines
                                expandedRows={this.state.expandedRows}
                                // onRowToggle={(e) => this.setState({ expandedRows: e.data })}
                                rowExpansionTemplate={this.rowExpansionTemplate}
                                dataKey="installmentId"
                            >
                                <Column body={this.expanderTemplate} style={{ width: '3rem' }} />
                                <Column header="Sl.No" body={(d, opts) => opts.rowIndex + 1} style={{ width:'3rem' }} className="text-center" />
                                <Column field="installmentName" header="INSTALLMENT" style={{ minWidth: '150px' }} />
                                <Column field="feeType" header="FEE TYPE" style={{ minWidth: '120px' }} body={(r) => r.feeType || '-'} />
                                <Column field="dueDate" header="DUE DATE" body={this.dateBodyTemplate} style={{ minWidth: '100px' }} />
                                <Column field="termAmount" header="AMT DUE" body={(r) => this.currencyBody(r.termAmount)} className="text-right" headerClassName="text-right" style={{ minWidth: '100px' }} />
                                <Column field="paid" header="PAID" body={(r) => this.currencyBody(r.paid)} className="text-right" headerClassName="text-right" style={{ minWidth: '100px' }} />
                                <Column field="balance" header="BALANCE" body={(r) => this.currencyBody(r.balance)} className="text-right font-bold" headerClassName="text-right" style={{ minWidth: '100px' }} />
                                <Column field="paymentRatio" header="RATIO (%)" body={(r) => <span className={r.paymentRatio >= 100 ? 'text-green-600 font-bold' : r.paymentRatio >= 50 ? 'text-orange-600' : 'text-red-500'}>{r.paymentRatio || 0}%</span>} className="text-center" style={{ minWidth: '80px' }} />
                                <Column field="status" header="STATUS" body={this.statusBodyTemplate} style={{ minWidth: '100px' }} />
                                <Column header="OVERDUE" body={(r) => r.overdueDays > 0 ? <span className="text-red-500 font-bold">{r.overdueDays} Days</span> : '-'} style={{ minWidth: '100px' }} />
                            </DataTable>
                        </div>
                    )}
                </Dialog>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    userInfo: state.currentUserPerms ? state.currentUserPerms.userInfo : {}
});

const FeeLedgerWithRouter = (props) => {
    const navigate = useNavigate();
    return <FeeLedger {...props} navigate={navigate} />;
};

export default connect(mapStateToProps)(FeeLedgerWithRouter);
